/**
 * Member Detail — customer point history drill-down + bonus points dialog.
 *
 * Shown when clicking a member in the Members list. Displays:
 * - Current balance + tier
 * - Full point transaction history (all LPE records)
 * - Award/deduct bonus points action
 *
 * Feature 10 — OPS-1833
 */

import { useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Minus,
  Coins,
  ShoppingBag,
  Gift,
  Cake,
  UserPlus,
  Star,
  CalendarCheck,
  Clock,
  ArrowDown,
  Pencil,
  Ticket,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  useCustomerPointHistory,
  useCustomerClaimedRewards,
  useAwardBonusPoints,
  type PointHistoryEntry,
  type ClaimedRewardItem,
  type MemberItem,
} from "../api/rewardsAdminHooks";

export const SOURCE_ICONS: Record<string, typeof Coins> = {
  Purchase: ShoppingBag,
  Signup: UserPlus,
  Birthday: Cake,
  Referral: Gift,
  "Daily Check-in": CalendarCheck,
  Bonus: Star,
  Manual: Plus,
  Deducted: Minus,
  Expired: Clock,
  Redemption: ArrowDown,
};

export const SOURCE_COLORS: Record<string, string> = {
  Purchase: "text-green-600",
  Signup: "text-blue-600",
  Birthday: "text-pink-500",
  Referral: "text-purple-600",
  "Daily Check-in": "text-amber-600",
  Bonus: "text-yellow-600",
  Manual: "text-sky-600",
  Deducted: "text-red-600",
  Expired: "text-gray-500",
  Redemption: "text-orange-600",
};

interface MemberDetailProps {
  member: MemberItem;
  /**
   * Back handler for the drill-down header. When omitted (e.g. embedded in the
   * Customer detail page's Loyalty tab, which already shows the customer name),
   * the back-arrow header is hidden.
   */
  onClose?: () => void;
}

export function MemberDetail({ member, onClose }: MemberDetailProps) {
  const historyQuery = useCustomerPointHistory(member.customer);
  const claimsQuery = useCustomerClaimedRewards(member.customer);
  const bonusMutation = useAwardBonusPoints();

  const [showBonus, setShowBonus] = useState(false);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [bonusReason, setBonusReason] = useState("");
  const [isDeduct, setIsDeduct] = useState(false);

  const handleAwardBonus = () => {
    if (bonusPoints <= 0) {
      toast.error("กรุณาระบุจำนวนแต้ม");
      return;
    }
    if (!bonusReason.trim()) {
      toast.error("กรุณาระบุเหตุผล");
      return;
    }

    const pts = isDeduct ? -bonusPoints : bonusPoints;
    bonusMutation.mutate(
      { customer: member.customer, points: pts, reason: bonusReason },
      {
        onSuccess: () => {
          toast.success(
            isDeduct
              ? `หักแต้ม ${bonusPoints} แต้มสำเร็จ`
              : `ให้แต้มโบนัส ${bonusPoints} แต้มสำเร็จ`,
          );
          setShowBonus(false);
          setBonusPoints(0);
          setBonusReason("");
        },
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Header — hidden when embedded (no onClose) */}
      {onClose && (
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{member.customer_name}</h3>
            <p className="text-xs text-muted-foreground">{member.customer}</p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border p-3 text-center">
          <p className="text-2xl font-bold">
            {historyQuery.data?.current_balance ?? member.points_balance}
          </p>
          <p className="text-xs text-muted-foreground">คะแนนคงเหลือ</p>
        </div>
        <div className="rounded-lg border p-3 text-center">
          <p className="text-lg font-semibold">{member.tier || "-"}</p>
          <p className="text-xs text-muted-foreground">เทียร์</p>
        </div>
        <div className="rounded-lg border p-3 text-center">
          <Button
            size="sm"
            className="w-full"
            onClick={() => setShowBonus(true)}
          >
            <Pencil className="mr-1 h-3 w-3" />
            ปรับแต้ม
          </Button>
        </div>
      </div>

      {/* Bonus points form (inline) */}
      {showBonus && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Button
              variant={!isDeduct ? "default" : "outline"}
              size="sm"
              onClick={() => setIsDeduct(false)}
            >
              <Plus className="mr-1 h-3 w-3" />
              ให้แต้ม
            </Button>
            <Button
              variant={isDeduct ? "destructive" : "outline"}
              size="sm"
              onClick={() => setIsDeduct(true)}
            >
              <Minus className="mr-1 h-3 w-3" />
              หักแต้ม
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">จำนวนแต้ม</Label>
              <Input
                type="number"
                min={1}
                value={bonusPoints || ""}
                onChange={(e) =>
                  setBonusPoints(Math.max(0, Number(e.target.value) || 0))
                }
                placeholder="100"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">เหตุผล</Label>
              <Input
                value={bonusReason}
                onChange={(e) => setBonusReason(e.target.value)}
                placeholder="เช่น ชดเชยปัญหาออเดอร์"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBonus(false)}
            >
              ยกเลิก
            </Button>
            <Button
              size="sm"
              onClick={handleAwardBonus}
              disabled={bonusMutation.isPending}
              variant={isDeduct ? "destructive" : "default"}
            >
              {bonusMutation.isPending && (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              )}
              {isDeduct ? "หักแต้ม" : "ให้แต้มโบนัส"}
            </Button>
          </div>
        </div>
      )}

      {/* Collected rewards */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">รางวัลที่แลก</h4>

        {claimsQuery.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        )}

        {claimsQuery.data?.claims.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            ยังไม่เคยแลกรางวัล
          </p>
        )}

        <div className="space-y-1">
          {claimsQuery.data?.claims.map((claim) => (
            <ClaimRow key={claim.name} claim={claim} />
          ))}
        </div>
      </div>

      {/* Point history */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">ประวัติคะแนน</h4>

        {historyQuery.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        )}

        {historyQuery.data?.entries.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            ยังไม่มีประวัติคะแนน
          </p>
        )}

        <div className="space-y-1">
          {historyQuery.data?.entries.map((entry) => (
            <HistoryRow key={entry.name} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}

export const REWARD_TYPE_CONFIG: Record<
  string,
  { icon: typeof Ticket; label: string; color: string }
> = {
  coupon: { icon: Ticket, label: "คูปอง", color: "text-purple-600" },
  free_item: { icon: Package, label: "ของแถม", color: "text-green-600" },
  custom: { icon: Gift, label: "รางวัลพิเศษ", color: "text-amber-600" },
};

function ClaimRow({ claim }: { claim: ClaimedRewardItem }) {
  const config = REWARD_TYPE_CONFIG[claim.reward_type] ?? REWARD_TYPE_CONFIG.custom;
  const Icon = config.icon;

  return (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`rounded-full p-1.5 bg-muted ${config.color}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div>
          <p className="text-sm font-medium">{claim.reward_title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{claim.claimed_at ?? "-"}</span>
            {claim.coupon_code && <span>• {claim.coupon_code}</span>}
            {claim.sales_order && <span>• {claim.sales_order}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">
          {config.label}
        </Badge>
        <Badge
          variant="secondary"
          className="bg-orange-100 text-orange-700 font-mono"
        >
          -{claim.points_spent.toLocaleString()}
        </Badge>
      </div>
    </div>
  );
}

function HistoryRow({ entry }: { entry: PointHistoryEntry }) {
  const Icon = SOURCE_ICONS[entry.earn_source] ?? Coins;
  const colorClass = SOURCE_COLORS[entry.earn_source] ?? "text-gray-600";
  const isPositive = entry.points > 0;

  return (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`rounded-full p-1.5 bg-muted ${colorClass}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div>
          <p className="text-sm font-medium">
            {entry.earn_source}
            {entry.description && (
              <span className="font-normal text-muted-foreground">
                {" "}
                — {entry.description}
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{entry.posting_date ?? "-"}</span>
            {entry.invoice && <span>• {entry.invoice}</span>}
            {entry.expiry_date && (
              <span>• หมดอายุ {entry.expiry_date}</span>
            )}
          </div>
        </div>
      </div>
      <Badge
        variant="secondary"
        className={
          isPositive
            ? "bg-green-100 text-green-700 font-mono"
            : "bg-red-100 text-red-600 font-mono"
        }
      >
        {isPositive ? "+" : ""}
        {entry.points.toLocaleString()}
      </Badge>
    </div>
  );
}
